import { computed, ref } from 'vue';
import type { RouteLocationNormalized } from 'vue-router';

/**
 * Generic composable for managing unsaved changes modal logic in the sharing hub.
 * Can be used for both partner and population views.
 */
export function useSharingHubModal() {
  const modalType = ref<'unsaved-changes' | null>(null);
  const nextRoute = ref<RouteLocationNormalized | null>(null);

  const showModal = computed(() => modalType.value !== null);

  const modalPrimaryButtonText = computed(() => 'Save Changes');

  const modalSecondaryButtonText = computed(() => 'Discard Changes');

  const modalTitle = computed(() => 'Unsaved changes');

  const modalDescription = computed(() => ({
    lines: ['You have unsaved changes.', 'What would you like to do?'],
  }));

  function openUnsavedChangesModal(route: RouteLocationNormalized) {
    nextRoute.value = route;
    modalType.value = 'unsaved-changes';
  }

  /**
   * Closes the modal and returns the pending route before clearing it.
   * This allows callers to navigate to the intended destination after closing.
   * @returns The route that was stored before closing, or null if none existed
   */
  function closeModal(): RouteLocationNormalized | null {
    const route = nextRoute.value;
    modalType.value = null;
    nextRoute.value = null;
    return route;
  }

  return {
    modalType,
    nextRoute,
    showModal,
    modalPrimaryButtonText,
    modalSecondaryButtonText,
    modalTitle,
    modalDescription,
    openUnsavedChangesModal,
    closeModal,
  };
}
